import type { modelState, ModelStateList } from "@/utils/modelState";
import type { SaveProgressInfo } from "./saveProgress";
import type { ProgramBookingState, ReservationProgramBookingDiff, ReservationProgramBookingDiffDto } from "./model.Programs";
import type { RatesRatePlanOverrides } from "./Rates/CustomRatePlan";
import type { RatesBaseRateOverrides } from "./Rates/CustomBaseRate";
import type { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import type { PaymentTypes } from "./PaymentTypes/PaymentType";
import type { RefundTypes } from "./RefundTypes/RefundType";
import type { ReservationStatus } from "./ReservationStatus";
import type { ReservationQuoteStatuses } from "./ReservationQuoteStatuses/ReservationQuoteStatus";
import type { ReservationLogEntry } from "./ReservationLogEntry";
import type { ReservationArrivalTransferMode } from "./ReservationArrivalTransferMode";
import type { ReservationDepartureTransferMode } from "./ReservationDepartureTransferMode";
import type { ReservationLogEntryDto } from "./ReservationLogEntry";
import type { ReservationPersonCache } from "./model.ReservationPerson";
import type { ReservationCompanyCache } from "./model.ReservationCompany";
import type { ReactNode } from 'react';

import type { AfterProfileSlotProps, AfterProfileSlotRenderFn, MacroConfiguration, ReservationState, MergeableRoomCluster, ReservationPerson, ReservationNewPerson, isReservationNewPerson, ReservationExistingPerson, LoadProfileResult, PersonCredit, RoomBookingState, AvailabilityCheckResult, ProgramAvailabilityCheckResult, RoomBooking, RoomPrice, PriceWithDiscount, RoomBlockDateDto, RoomBlockDate, RoomBlockDateRoom, CalculatedPrice, CalculatedRowPrice, RoomBookingDate, RoomBookingDateDto, RoomBookingDateVoucher, RoomBookingDateVoucherEffect, PriceBreakdownContainer, SplitAmountsContainer, AmountWithSource, MultiplierWithSource, RoomBookingPerson, Voucher, SaveReservationRequestVoucher, ReservationDiffContainer, RoomSplitInfo, ReservationRoomBookingDiff, ReservationPosItemDiff, DiffTransaction, ReservationPosItemDto, ReservationItemSplitMode, ProgramSplitMode, RoomSplitMode, CheckPriceResult, CheckPriceResultRoom, AvailabilityCheckResultDto, CheckPriceResultProgram, ProgramAvailabilityCheckResultDto, ReservationDiffContainerDto, ReservationRoomBookingDiffDto, ReservationPosItemDiffDto, ReservationLogEntryContainerDto, ReservationRefundDto, ReservationRefundTransactionDto, ReservationPaymentDto, ReservationPaymentTransactionDto, ReservationStatusEnum, VersionHistoryResponse, VersionSummary, VersionRoomSummary, VersionProgramSummary, VersionRoomAvailability, VersionProgramAvailability } from "./types";

export interface ExternalPostingStatus {
    Verdict: string;
    Reason: string | null;
    DeepLink: string | null;
    // Per-document journal links of a rolled-up document set (e.g. a VAT-report void row over its base
    // TN/CN + every -V correction). Null on a single-document row; the badge falls back to DeepLink.
    Journals?: QbJournalLink[] | null;
}

export interface QbJournalLink {
    DocumentNumber: string;
    DeepLink: string;
}

export interface ReservationPayment {
    CreatedDate: Date;
    PaymentId: string;
    PaymentType: PaymentTypes.PaymentType;
    Amount: number;
    Transactions: ReservationPaymentTransaction[];
}

export interface ReservationPaymentTransaction {
    CreatedDate: Date;
    EffectiveDate: Date;
    Amount: number;
    Note: string;
    TaxDocument: TaxDocumentTypes.TaxDocumentType;
    IsVoid?: boolean;
    // Document whose journal this row maps to: {base}-V{seq} for a void/restore toggle, else the base
    // TN/CN. The join key for this row's own posting status + the manual-post target. Null when no doc.
    JournalDocumentNumber?: string | null;
    ExternalPostingStatus?: ExternalPostingStatus | null;
}

export interface ReservationRefund {
    CreatedDate: Date;
    RefundId: string;
    RefundType: RefundTypes.RefundType;
    Amount: number;
    Transactions: ReservationRefundTransaction[];
}

export interface ReservationRefundTransaction {
    CreatedDate: Date;
    EffectiveDate: Date;
    Amount: number;
    Note: string;
    TaxDocument: TaxDocumentTypes.TaxDocumentType;
    IsVoid?: boolean;
    // See ReservationPaymentTransaction.JournalDocumentNumber.
    JournalDocumentNumber?: string | null;
    ExternalPostingStatus?: ExternalPostingStatus | null;
}

export interface RevenueItem {
    DateStatus: "LOCKED" | "CURRENT" | "FUTURE";
    CreatedDate: Date;
    EffectiveDate: Date;
    Account: string;
    Description: string;
    Amount: number;
    ExternalPostingStatus?: ExternalPostingStatus | null;
}

export interface ReservationPosItem {
    Description: string;
    Quantity: number;
    Amount: number;
    ReservationOrderId: string;
    ReservationOrderLineItemId: string;
    RevenueDate: Date;
    IsRevenueLocked: boolean;
    CanRemove: boolean;
    CanChangeAmount: boolean;
    IsPackage: boolean;
}

export interface ReservationQuote {
    ExpiresAt: modelState<Date>;
    Status: modelState<ReservationQuoteStatuses.ReservationQuoteStatus>;
    StatusIsDirty: modelState<boolean>;
    StatusVersion: modelState<number>;
    TempReservationEnabled: modelState<boolean>;
    TempReservationReleaseAfter: modelState<Date | null>;
}

export interface ReservationNote {
    NoteId: string;
    NoteText: string;
    CreatedBy: string;
    CreatedAt: Date;
}

export interface ReservationLogEntryContainer {
    LogEntryId: string;
    LogText: string;
    LogData: ReservationLogEntry;
    CreatedBy: string;
    CreatedAt: Date;
}

export interface ArrivalTransferDetailsState {
    Mode: modelState<ReservationArrivalTransferMode | null>;
}

export interface DepartureTransferDetailsState {
    Mode: modelState<ReservationDepartureTransferMode | null>;
}

export interface NewPersonDto {
    PersonId: string;
    FirstName: modelState<string>;
    LastName: modelState<string | null>;
    PhoneNumber: modelState<string | null>;
    Email: modelState<string | null>;
    PersonDto: PersonDto & { [key: string]: Object };
}

export interface NewCompanyDto {
    CompanyId: string;
    Name: modelState<string>;
    CompanyDto: CompanyDto & { [key: string]: Object };
}

export interface PersonDto {
    PersonId: string;
    ContactId: string | null;
    FirstName: string;
    LastName: string | null;
    PhoneNumber: string | null;
    Email: string | null;
    Credits: PersonCredit[];
}

/**
 * Company support (for billing contact)
 * A company only has a Name at the top level.
 */
export interface CompanyDto {
    CompanyId: string;
    ContactId: string | null;
    Name: string;
}

// Reservation Contact wrappers
export const ReservationCompany = Symbol("ReservationCompany");
export interface ReservationCompany {
    [ReservationCompany]: boolean;
}

export const ReservationNewCompany = Symbol("ReservationNewCompany");
export interface ReservationNewCompany extends ReservationCompany {
    [ReservationNewCompany]: boolean;
    Name: string;
}

export function isReservationNewCompany(val: ReservationCompany | ReservationPerson): val is ReservationNewCompany {
    return !!val && (val as any)[ReservationNewCompany] === true;
}

export const ReservationExistingCompany = Symbol("ReservationExistingCompany");
export interface ReservationExistingCompany extends ReservationCompany {
    [ReservationExistingCompany]: boolean;
    CompanyId: string;
    ContactId: string | null;
}

export function isReservationExistingCompany(val: ReservationCompany | ReservationPerson): val is ReservationExistingCompany {
    return !!val && (val as any)[ReservationExistingCompany] === true;
}

// A ReservationContact can be a Person or a Company
export type ReservationContact = ReservationPerson | ReservationCompany;

export interface RoomType {
    Key: string;
    Name: string;
    RoomTypeLocationId: string;
    Rooms: { [roomLocationId: string]: RoomTypeRoom };
    RoomFormFieldRefs: RoomFormFieldRefDto[];
}

export interface RoomFormFieldRefDto {
    FormId: string;
    FieldId: string;
}

export interface RoomTypeRoom {
    Name: string;
    RoomLocationId: string;
}

export interface ProgramType {
    Key: string;
    Name: string;
}

export interface OccupantType {
    Key: string;
    Name: string;
}

export interface RoomBlockDatesContainer {
    Dates: ModelStateList<RoomBlockDate>;
    Version: modelState<number>;
}

export interface RatesContainerDto {
    RatePlans: RatesRatePlanDto[];
    BaseRates: RatesBaseRateDto[];
    Multipliers: RatesRateMultiplierDto[];
    Discounts: RatesRateDiscountDto[];
}

export interface RatesRatePlanDto {
    RatePlanId: string;
    RatePlanVersion: number;
    Name: string;
    VisibleInReservations: boolean;
    IsHistorical: boolean;
    CreatedAt: string;
    FormId: string | null;
    FormFields: RatePlanFormFieldDto[] | null;
}

export interface RatePlanFormFieldDto {
    FieldId: string;
    DisplayName: string;
    Options: RatePlanFormFieldOptionDto[];
}

export interface RatePlanFormFieldOptionDto {
    Value: string;
    Text: string;
}

export interface RatePlanFormResponse {
    FormId: string;
    Fields: RatePlanFieldValue[];
}

export interface RatePlanFieldValue {
    FieldName: string;
    Value: string;
}

export interface RatesBaseRateDto {
    BaseRateId: string;
    BaseRateVersion: number;
    Name: string;
    VisibleInReservations: boolean;
}

export interface RatesRateMultiplierDto {
    RateMultiplierId: string;
    RateMultiplierVersion: number;
    Name: string;
    VisibleInReservations: boolean;
}

export interface RatesRateDiscountDto {
    RateDiscountId: string;
    RateDiscountVersion: number;
    Name: string;
    VisibleInReservations: boolean;
}

export interface RatePlanKey {
    RatePlanId: string;
    RatePlanVersion: number;
}
